import React, { useState, useEffect } from "react";
import Select from "react-select";
import apiClient from "../utils/apiClient";
import { SERVER_BASE } from "../config/server";
import moment from "moment";
import DateRangePickerComponent from "./CustomDateRangePicker";
import { useLocation, useNavigate } from "react-router-dom";
import ExcelJS from "exceljs";
import { saveAs } from "file-saver";
import Swal from "sweetalert2";

const ConsolidatedReport = () => {
  const [departments, setDepartments] = useState([]);
  const [dateRange, setDateRange] = useState([null, null]);
  const [selectedDept, setSelectedDept] = useState([]);
  const [reportData, setReportData] = useState({});
  const [loading, setLoading] = useState(false);
  const [qtyFilter, setQtyFilter] = useState("both"); 

  const location = useLocation();
  const navigate = useNavigate();
  const accessOfDepartments = location.state?.accessOfDepartments || "";

  /* Load departments */
  useEffect(() => {
    if (accessOfDepartments) {
      const deptArray = accessOfDepartments
        .split(",")
        .map((dept) => ({
          value: dept.trim(),
          label: dept.trim(),
        }));

      // Add "Select All" option
      setDepartments([{ value: "__ALL__", label: "Select All" }, ...deptArray]);
    }
  }, [accessOfDepartments]);

  /* Handle Select All logic */
  const handleDeptChange = (selected) => {
    if (!selected) {
      setSelectedDept([]);
      return;
    }

    const clickedSelectAll = selected.find((opt) => opt.value === "__ALL__");

    if (clickedSelectAll) {
      // IF full list selected → unselect all
      if (selected.length - 1 === departments.length - 1) {
        setSelectedDept([]);
      } else {
        // Select all except "__ALL__"
        const allDepts = departments.filter((d) => d.value !== "__ALL__");
        setSelectedDept(allDepts);
      }
    } else {
      setSelectedDept(selected);
    }
  };

  const getReport = async () => {
    if (selectedDept.length === 0) {
       Swal.fire({
              toast: true,
              position: "top-end",
              showConfirmButton: false,
              timerProgressBar: true,
              icon: "error",
              text: "Please select atleast one department",
              timer: 3000,
            });
      return;
    }
    if (!dateRange[0] || !dateRange[1]) {
      Swal.fire({
        toast: true,
        position: "top-end",
        showConfirmButton: false,
        timerProgressBar: true,
        icon: "error",
        text: "Please select date range",
        timer: 3000,
      });
      return;
    }

    const selected = selectedDept.map((d) => d.value).join(",");

    setLoading(true);
    try {
      const res = await apiClient.get(
        `${SERVER_BASE}/getConsolidatedProjectRequirement`,
        {
          params: {
            departments: selected,
            startDate: dateRange[0],
            endDate: dateRange[1],
          },
        }
      );

      setReportData(res.data);
    } catch (err) {
      console.error("Error fetching consolidated data:", err);
    } finally {
      setLoading(false);
    }
  };
  const addExcelHeader = (sheet, title1, title2, totalColumns) => {
    // Title 1 (Bold, Big, Center)
    sheet.mergeCells(1, 1, 1, totalColumns);
    const header1 = sheet.getCell(1, 1);
    header1.value = title1;
    header1.alignment = { horizontal: "center", vertical: "middle" };
    header1.font = { size: 16, bold: true };
  
    // Title 2 (Bold, Medium)
    sheet.mergeCells(2, 1, 2, totalColumns);
    const header2 = sheet.getCell(2, 1);
    header2.value = title2;
    header2.alignment = { horizontal: "center", vertical: "middle" };
    header2.font = { size: 14, bold: true };
  
    // Add spacing row before table header
    sheet.addRow([]);
    sheet.addRow([]);
  };
  

  /* Export To Excel */
  const exportToExcel = async () => {
    if (!reportData || Object.keys(reportData).length === 0) {
      Swal.fire({
        toast: true,
        position: "top-end",
        showConfirmButton: false,
        timerProgressBar: true,
        icon: "warning",
        text: "No data to export",
        timer: 3000,
      });
      return;
    }
  
    const workbook = new ExcelJS.Workbook();
  
    Object.keys(reportData).forEach((dept) => {
      const filteredRows = filterRowsByQty(reportData[dept]);

      if (filteredRows.length === 0) return; // 🚫 skip sheet
      const sheet = workbook.addWorksheet(dept);
  
      // Dynamic columns based on filter (UOM column removed)
      const cols = [
        { header: "S.No", key: "sno", width: 8 },
        { header: "Date", key: "date", width: 15 },
        { header: "PR Code", key: "requirementCode", width: 20 },

        { header: "Name of Articles", key: "printName", width: 30 },
      ];
  
      if (qtyFilter !== "local")
        cols.push({ header: "Zonal Qty", key: "zonalQty", width: 15 });
  
      if (qtyFilter !== "zonal")
        cols.push({ header: "Local Qty", key: "localQty", width: 15 });
  
      if (qtyFilter === "both")
        cols.push({ header: "Total Qty", key: "totalQty", width: 15 });
  
      cols.push({ header: "Remarks", key: "remarks", width: 30 });
  
      sheet.columns = cols;
      // ADD MAIN TITLES
addExcelHeader(
  sheet,
  "REQUIREMENT OF MATERIAL",
  "FOR BABAJI's VISIT PROGRAMME - FEBRUARY 2026",
  cols.length
);

// Now add table header row
sheet.addRow(Object.fromEntries(cols.map(c => [c.key, c.header])));

// Apply bold to table header
sheet.getRow(sheet.lastRow.number).font = { bold: true };
      let index = 1;
      filterRowsByQty(reportData[dept]).forEach((row) => {

        const excelRow = {
          sno: index++,
          date: moment(row.date).format("DD-MMM-YY"),
          requirementCode: row.requirementCode,
          printName: row.printName,
          remarks: row.remarks,
        };
  
        if (qtyFilter != "local")
          excelRow.zonalQty = `${cleanNumber(row.zonalQty)} ${row.zonalQty?row.uom:''}`;
  
        if (qtyFilter != "zonal")
          excelRow.localQty = `${cleanNumber(row.localQty)} ${row.localQty?row.uom:''}`;
  
        if (qtyFilter == "both")
          excelRow.totalQty = `${cleanNumber(row.totalQty)} ${row.uom}`;
  
        sheet.addRow(excelRow);
      });
  
      sheet.getRow(1).font = { bold: true };
    });
  
    const buffer = await workbook.xlsx.writeBuffer();
    saveAs(
      new Blob([buffer], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      }),
      "Project_Requirement.xlsx"
    );
  };

  const exportMergedExcel = async () => {
    
    if (!reportData || Object.keys(reportData).length === 0) {
      Swal.fire({
        toast: true,
        position: "top-end",
        showConfirmButton: false,
        timerProgressBar: true,
        icon: "warning",
        text: "No data to export",
        timer: 3000,
      }); 
      return;
    }
  
    const workbook = new ExcelJS.Workbook();
    const sheet = workbook.addWorksheet("Merged Report");
  
    // Dynamic columns (DEPARTMENT column removed)
    const cols = [
      { header: "S.No", key: "sno", width: 8 },
      { header: "Date", key: "date", width: 15 },
      { header: "PR Code", key: "requirementCode", width: 20 },
      { header: "Name of Articles", key: "printName", width: 30 },
    ];
  
    if (qtyFilter !== "local")
      cols.push({ header: "Zonal Qty", key: "zonalQty", width: 15 });
  
    if (qtyFilter !== "zonal")
      cols.push({ header: "Local Qty", key: "localQty", width: 15 });
  
    if (qtyFilter === "both")
      cols.push({ header: "Total Qty", key: "totalQty", width: 15 });
  
    cols.push({ header: "Remarks", key: "remarks", width: 40 });
  
    sheet.columns = cols;
  
    // ADD MAIN TITLES
    addExcelHeader(
      sheet,
      "REQUIREMENT OF MATERIAL",
      "FOR BABAJI's VISIT PROGRAMME - FEBRUARY 2026",
      cols.length
    );
  
    // --- LOOP DEPARTMENTS ---
    Object.keys(reportData).forEach((dept) => {
      const filteredRows = filterRowsByQty(reportData[dept]);

  if (filteredRows.length === 0) return; // 🚫 skip department section
      // 1️⃣ MERGED DEPARTMENT TITLE (center + bold)
      sheet.addRow([]);
      const deptTitleRow = sheet.addRow([`ARTICLES FOR ${dept.toUpperCase()} DEPARTMENT`]);
  
      deptTitleRow.font = { bold: true, size: 13 };
      deptTitleRow.alignment = { horizontal: "center", vertical: "middle" };
  
      // Merge all columns for dept header
      sheet.mergeCells(
        deptTitleRow.number,
        1,
        deptTitleRow.number,
        cols.length
      );
  
      // Light highlight
      deptTitleRow.fill = {
        type: "pattern",
        pattern: "solid",
        fgColor: { argb: "FFF4E5" },
      };
  
      sheet.addRow([]);
  
      // 2️⃣ ADD TABLE HEADERS
      sheet.addRow(Object.fromEntries(cols.map(c => [c.key, c.header])));
      const headerRow = sheet.lastRow;
      headerRow.font = { bold: true };
      headerRow.alignment = { horizontal: "center" };
  
      // 3️⃣ ADD ROWS FOR THIS DEPARTMENT
      let index = 1;
      filterRowsByQty(reportData[dept]).forEach((row) => {
        const excelRow = {
          sno: index++,
          date: moment(row.date).format("DD-MMM-YY"),
          requirementCode: row.requirementCode,
          printName: row.printName,
          remarks: row.remarks,
        };
  
        if (qtyFilter !== "local")
          excelRow.zonalQty = `${cleanNumber(row.zonalQty)} ${row.zonalQty?row.uom:""}`;
  
        if (qtyFilter !== "zonal")
          excelRow.localQty = `${cleanNumber(row.localQty)} ${row.localQty?row.uom:''}`;
  
        if (qtyFilter === "both")
          excelRow.totalQty = `${cleanNumber(row.totalQty)} ${row.uom}`;
  
        const r = sheet.addRow(excelRow);
  
        // Center-align all qty columns
        r.alignment = { horizontal: "center" };
        r.getCell("printName").alignment = { horizontal: "left" }; 
        r.getCell("remarks").alignment = { horizontal: "left" };
      });
  
      sheet.addRow([]); // spacing
    });
  
    const buffer = await workbook.xlsx.writeBuffer();
    saveAs(
      new Blob([buffer], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      }),
      "Project_Requirement.xlsx"
    );
  };
  
  
  
  
  const cleanNumber = (num) => {
    if (num == null) return "-";
    const n = Number(num);
    return n % 1 == 0 ? n.toFixed(0) : n.toString();
  };
  const filterRowsByQty = (rows) => {
    return rows.filter((row) => {
      if (qtyFilter === "zonal") {
        return row.zonalQty != null && Number(row.zonalQty) > 0;
      }
  
      if (qtyFilter === "local") {
        return row.localQty != null && Number(row.localQty) > 0;
      }
    
      
      // both
      return (
        (row.zonalQty != null && Number(row.zonalQty) > 0) ||
        (row.localQty != null && Number(row.localQty) > 0)
      );
    });
  };
  

  return (
    <div className="container mt-4">
             <button className="btn btn-secondary" onClick={() => navigate(-1)}>
          Back
        </button> <h3>Consolidated Report</h3>
      <div className="row mt-4 align-items-end g-2">

{/* Date Range */}
<div className="col-md-2">
  <label className="form-label">Date Range</label>
  <DateRangePickerComponent value={dateRange} onChange={setDateRange} />
</div>

{/* Department Multi Select */}
<div className="col-md-4">
  <label className="form-label">Departments</label>
  <Select
    options={departments}
    isMulti
    value={selectedDept}
    onChange={handleDeptChange}
  />
</div>

{/* Qty Type */}
<div className="col-md-2">
  <label className="form-label">Qty Type</label>
  <select 
    className="form-select"
    value={qtyFilter}
    onChange={(e) => setQtyFilter(e.target.value)}
  >
    <option value="both">Both</option>
    <option value="zonal">Zonal Only</option>
    <option value="local">Local Only</option>
  </select>
</div>

{/* Get Button */}
<div className="col-md-1">
  <button
    className="btn btn-primary w-100"
    onClick={getReport}
    disabled={loading}
  >
    {loading ? "Loading..." : "Get"}
  </button>
</div>

{/* Excel */}
<div className="col-md-1">
  <button className="btn btn-success w-100" onClick={exportToExcel}>
    Excel-Tabs
  </button>
</div>

{/* Excel Merged */}
<div className="col-md-2">
  <button className="btn btn-warning" onClick={exportMergedExcel}>
    Excel - Merged
  </button>
</div>

</div>


      {/* Data Output */}
      <div className="mt-4">
      {Object.keys(reportData).map((dept) => {
  const filteredRows = filterRowsByQty(reportData[dept]);

  // 🚫 Skip department completely if no rows
  if (filteredRows.length === 0) return null;

  return (
    <div key={dept} className="mb-4">
      <h5 className="text-primary text-center">
        Articles for {dept} Department
      </h5>

      <table className="table table-bordered text-center align-middle">
        <thead>
          <tr>
            <th>S.No</th>
            <th>Date</th>
            <th>PR Code</th>
            <th>Name of Articles</th>

            {qtyFilter !== "local" && <th>Zonal Qty</th>}
            {qtyFilter !== "zonal" && <th>Local Qty</th>}
            {qtyFilter === "both" && <th>Total Qty</th>}

            <th>Remarks</th>
          </tr>
        </thead>

        <tbody>
          {filteredRows.map((row, i) => (
            <tr key={i}>
              <td>{i + 1}</td>
              <td>{moment(row.date).format("DD-MMM-YY")}</td>
              <td>{row.requirementCode}</td>
              <td>{row.printName}</td>

              {qtyFilter !== "local" && (
                <td>{cleanNumber(row.zonalQty)} {row.zonalQty ? row.uom : ""}</td>
              )}

              {qtyFilter !== "zonal" && (
                <td>{cleanNumber(row.localQty)} {row.localQty ? row.uom : ""}</td>
              )}

              {qtyFilter === "both" && (
                <td>{cleanNumber(row.totalQty)} {row.uom}</td>
              )}

              <td>{row.remarks}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
})}

      </div>
    </div>
  );
};

export default ConsolidatedReport;
